import { Typography } from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { LabeledSelect } from 'components/views/atoms/LabeledSelect';
import { LabeledNumberField } from 'components/views/atoms/LabeledNumberField';
import { MediaDevice, MicrophoneDevice } from 'core/models/mediaDevice';
import { cancellable } from 'services/utility/cancellable';
import { SyncButton } from 'components/views/atoms/SyncButton';
import { SafeBox } from '../../atoms/SafeBox';

const codecs: MediaDevice.AudioCodec[] = [
    'OPUS',
];

const scale = 1000;

function applyScale(v: number | undefined) {
    return (v === undefined) ? undefined : Math.floor(v / scale);
}

function equalConfig(a: Partial<MediaDevice.AudioConfig>, b: Partial<MediaDevice.AudioConfig>) {
    if (a.enabled !== b.enabled) return false;
    if (applyScale(a.maxBitrate) !== applyScale(b.maxBitrate)) return false;
    if (applyScale(a.minBitrate) !== applyScale(b.minBitrate)) return false;
    return true;
}

export type AudioStreamConfigFormProps = {
    microphone?: MicrophoneDevice;
    disabled?: boolean;
    getAudioConfig?: (mediaDeviceId: MediaDevice.Id) => Promise<Partial<MediaDevice.AudioConfig>>;
    putAudioConfig?: (mediaDeviceId: MediaDevice.Id, config: Partial<MediaDevice.AudioConfig>) => Promise<void>;
}

export function AudioStreamConfigForm(props: AudioStreamConfigFormProps) {
    const { microphone, disabled = false, getAudioConfig, putAudioConfig } = props;

    const [remoteConfig, setRemoteConfig] = useState<Partial<MediaDevice.AudioConfig>>();
    const [config, setConfig] = useState<Partial<MediaDevice.AudioConfig>>({});
    const [dirty, setDirty] = useState(false);
    const [syncing, setSyncing] = useState(false);

    useEffect(() => {
        setConfig({});
        setDirty(true);
    }, [microphone]);

    useEffect(() => {
        if (!getAudioConfig || !microphone) return;
        if (!dirty) return;

        setSyncing(true);

        return cancellable(async isCancel => {
            const config = await getAudioConfig(microphone.id);
            if (isCancel()) return;
            setRemoteConfig(config);
            setConfig(config);
            setSyncing(false);
            setDirty(false);
        });
    }, [getAudioConfig, microphone, dirty]);

    const [modified, setModified] = useState(false);

    const modify = useCallback((config: Partial<MediaDevice.AudioConfig>) => {
        setConfig(prev => {
            const updated = { ...prev, ...config };
            if (remoteConfig && equalConfig(remoteConfig, updated)) {
                setModified(false);
                return remoteConfig;
            } else {
                setModified(true);
                return updated;
            }
        });
    }, [remoteConfig]);

    const update = useCallback(() => {
        console.log({ update, putConfig: putAudioConfig, microphone, config });
        if (!putAudioConfig || !microphone || !config) return;

        setSyncing(true);

        return cancellable(async isCancel => {
            await putAudioConfig(microphone.id, config);
            if (isCancel()) return;
            setModified(false);
            setDirty(true);
        });
    }, [putAudioConfig, microphone, config]);


    const codec = config && 'codec' in config ? config.codec : undefined;

    return (
        <SafeBox sx={{
            marginLeft: theme => theme.spacing(0.5),
            marginRight: theme => theme.spacing(0.5),
        }}>
            <SafeBox>
                <Typography variant='body1'>
                    <span>audio settings </span>
                    <SyncButton
                        syncing={syncing}
                        hidden={!syncing && !modified}
                        disabled={putAudioConfig === undefined}
                        onClick={update} />
                </Typography>
            </SafeBox>
            <SafeBox sx={{
                marginLeft: '1em',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
            }}>
                <LabeledSelect
                    label='codec'
                    disabled={disabled || syncing}
                    options={codecs}
                    selected={codec}
                    onChange={codec => modify({ codec })} />
                <LabeledNumberField
                    label='max bitrate'
                    disabled={disabled || syncing}
                    value={config?.maxBitrate}
                    scale={scale}
                    suffix='kbps'
                    onChange={maxBitrate => modify({ maxBitrate })} />
            </SafeBox>
        </SafeBox>
    );
}
